import React, { useEffect, useState } from 'react';
import {
	tempFlashbarMessage,
	ToggleableDatetime,
	ConfirmActionModal,
	CustomStatusIndicator,
} from 'src/components/helpers';
import { Box, Button, Header, SpaceBetween, Table, Toggle } from '@amzn/awsui-components-console';
import store, { useAlias, actions, useGroupName } from 'src/data/redux';
import { StatusCode } from 'src/constants/hammerstoneConstants';
import PAGES from 'src/nav/Pages';
import InternalLink from '../helpers/InternalLink';
import { DeleteActivityRevisionInfo, GetActivityRevisions, PutActivityInfo } from 'src/lib/hammerstoneApi';
import keys from 'src/constants/hammerstoneConstantKeys';
import { ActivityRevisionSummary } from '@amzn/aws-hammerstone-exposed-restful-service-typescript-client/clients/hammerstoneexposedrestfulservicelambda';

export default function ActivityHistory(props: { activityId: string; refreshActivity: Function }) {
	const activityId = parseInt(props.activityId);
	const groupName = useGroupName();
	const alias = useAlias();
	const [loading, setLoading] = useState<boolean>(true);
	const [showDeleted, setShowDeleted] = useState<boolean>(false);
	const [selectedItem, setSelectedItem] = useState<ActivityRevisionSummary>();
	const [items, setItems] = useState<ActivityRevisionSummary[]>();
	const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
	const [revertModalVisible, setRevertModalVisible] = useState<boolean>(false);

	const selectedRevision = selectedItem?.revision;
	const disabled = !selectedRevision;
	const lastRevision = items?.at(-1)?.revision;

	/**
	 * Fetch the activity's revisions from the GetActivityRevisions API
	 * and set the `items` state to them to populate the table
	 */
	function refreshRevisions() {
		setLoading(true);
		GetActivityRevisions({ activityId, groupName, showDeleted })
			.then((output) => {
				setItems(output?.revisions);
				setSelectedItem(null);
			})
			.catch((error) => {
				store.dispatch(
					actions.page.addToFlashbar({
						id: 'get_activity_revisions_failure',
						message: {
							type: 'error',
							dismissible: true,
							header: `Failed to retrieve revisions for activity ${activityId}`,
							content: error.message || 'An unexpected error occurred while fetching activity revisions.',
						},
					})
				);
			})
			.finally(() => {
				setLoading(false);
			});
	}

	/**
	 * Attempt to soft-delete a particular activity revision
	 * and communicate success/failures to customers with flashbars
	 */
	function handleDeleteRevision() {
		DeleteActivityRevisionInfo({
			groupName,
			activityId,
			revision: selectedRevision,
		})
			.then(() => {
				tempFlashbarMessage({
					id: 'delete_activity_revision',
					message: {
						header: `Successfully deleted revision ${selectedRevision} of activity ${activityId}`,
						content:
							'To restore a deleted revision click "Show deleted", select the desired revision, and then click "Revert"',
					},
				});
			})
			.catch((error) => {
				console.error('Error occurred while deleting revision', error);
				store.dispatch(
					actions.page.addToFlashbar({
						id: 'delete_activity_revision_error',
						message: {
							type: 'error',
							dismissible: true,
							header: `Failed to delete revision ${selectedRevision} of activity ${activityId}`,
							content: error?.message || 'An unexpected error occurred while deleting the revision.',
						},
					})
				);
			})
			.finally(() => {
				// Update the revisions page to reflect this change
				refreshRevisions();
			});
	}

	/**
	 * Attempt to revert the activity's current draft to the selected revision
	 * and communicate success/failures to customer with flashbars
	 */
	function handleRevertRevision() {
		PutActivityInfo({
			activityId,
			groupName,
			body: {
				operation: keys.Operation.revert,
				updatedBy: alias,
				revision: selectedRevision,
			},
		})
			.then(() => {
				tempFlashbarMessage({
					id: 'revert_activity_revision',
					message: {
						header: `Successfully reverted to ${selectedRevision} of activity ${activityId}`,
						content: 'Activity is now in "Edit pending", to activate this version click "Activate" above',
					},
				});
				props.refreshActivity();
			})
			.catch((error) => {
				store.dispatch(
					actions.page.addToFlashbar({
						id: 'revert_activity_revision_failure',
						message: {
							type: 'error',
							dismissible: true,
							header: `Failed to revert to revision ${selectedRevision} of activity ${activityId}`,
							content: error.message || 'An unexpected error occurred while reverting the revision.',
						},
					})
				);
			})
			.finally(() => {
				// Update the revisions page to reflect this change
				refreshRevisions();
			});
	}

	useEffect(() => {
		if (props.activityId && groupName) {
			refreshRevisions();
		}
	}, [props.activityId, groupName, showDeleted]);

	return (
		<SpaceBetween size="l">
			<Header variant="h2">Activity history</Header>
			<Table
				header={
					<Header
						variant="h3"
						actions={
							<SpaceBetween direction="horizontal" size="xs">
								<Box padding="xxs">
									<Toggle
										disabled={loading}
										checked={showDeleted}
										onChange={({ detail }) => {
											setShowDeleted(detail.checked);
										}}
									>
										Show deleted
									</Toggle>
								</Box>
								<Button
									disabled={disabled || selectedItem?.deleted}
									loading={loading}
									iconName="remove"
									onClick={() => setDeleteModalVisible(true)}
								>
									Delete
								</Button>
								<Button
									disabled={disabled}
									loading={loading}
									iconName="undo"
									onClick={() => setRevertModalVisible(true)}
								>
									Revert
								</Button>
							</SpaceBetween>
						}
					>
						Revisions
					</Header>
				}
				columnDefinitions={[
					{
						id: 'revision',
						header: 'Revision',
						cell: ({ revision, deleted }) => {
							switch (revision) {
								case 0:
									return '0 (initial)';
								case lastRevision:
									return (
										<span>
											{revision}{' '}
											<InternalLink
												// Navigate to Details section in new website for the current revision
												href={PAGES.VIEW_ACTIVITY.path}
												pathParams={{ activityId }}
												groupName
											>
												(current)
											</InternalLink>
										</span>
									);
								default:
									return (
										<span>
											<InternalLink
												groupName
												href={PAGES.DIFF_ACTIVITY_REVISION.path}
												pathParams={{ activityId, revision, diffRevision: lastRevision }}
											>
												{revision}
											</InternalLink>
											{deleted && ' (deleted)'}
										</span>
									);
							}
						},
					},
					{
						id: 'revisionComment',
						header: 'Comment',
						cell: (item) => item.comment || <small>(none)</small>,
					},
					{
						id: 'updateDate',
						header: 'Last updated',
						cell: (item) => <ToggleableDatetime>{item.updateDate}</ToggleableDatetime>,
					},
					{ id: 'updatedBy', header: 'Updated by ', cell: (item) => item.updatedBy },
				]}
				items={items}
				isItemDisabled={(item) => !item.revision || item?.revision === lastRevision}
				empty={'No revisions found'}
				loading={loading}
				loadingText="Fetching activity revision history"
				selectionType="single"
				selectedItems={[selectedItem]}
				onSelectionChange={({ detail }) => {
					setSelectedItem(detail.selectedItems[0]);
				}}
			/>

			<ConfirmActionModal
				visible={deleteModalVisible}
				setVisible={setDeleteModalVisible}
				onConfirm={handleDeleteRevision}
				header={`Delete revision ${selectedRevision} of activity ${activityId}`}
				confirmName="Delete"
			>
				Are you sure you want to delete revision {selectedRevision}?
			</ConfirmActionModal>

			<ConfirmActionModal
				visible={revertModalVisible}
				setVisible={setRevertModalVisible}
				onConfirm={handleRevertRevision}
				header={`Revert to revision ${selectedRevision} of activity ${activityId}`}
				confirmName="Revert"
			>
				<SpaceBetween size="m">
					<Header variant="h3">Are you sure you want to revert to revision {selectedRevision}?</Header>
					<Box>
						This will create a new draft with revision {selectedRevision}'s configuration, setting your
						activity to <CustomStatusIndicator>{StatusCode.EDIT_PENDING}</CustomStatusIndicator>. To commit
						these changes after reverting, you must activate the activity.
					</Box>
					<Box>
						NOTE: If your activity is ALREADY in{' '}
						<CustomStatusIndicator>{StatusCode.EDIT_PENDING}</CustomStatusIndicator>, reverting to a
						previous revision will <b>overwrite</b> any uncommitted changes.
					</Box>
				</SpaceBetween>
			</ConfirmActionModal>
		</SpaceBetween>
	);
}
